// Background removal, start to finish, inside the browser.
//
// A U-2-Net model looks at a 320x320 copy of the picture and answers with a
// saliency map: one number per pixel saying how much that pixel belongs to the
// subject. That map, scaled back up, becomes the alpha channel of the original.
//
// Loading the model and the runtime is tools/onnx.js, shared with the upscaler.
// Nothing is sent anywhere: the only requests either module makes are for two
// static files on this origin, and after the first visit the Cache Storage
// answers even those.

import { loadRuntime, sessionFor } from './onnx.js';

// What the network was trained on. Both models take the same input, so the
// numbers below are not per-model.
const INPUT_SIZE = 320;
const MEAN = [0.485, 0.456, 0.406];
const STD = [0.229, 0.224, 0.225];

// Above this on the long edge the result is scaled down before the mask is
// applied. A 48 megapixel photo needs three buffers of four bytes per pixel at
// once, which is where a phone browser stops rather than slows down.
const MAX_OUTPUT_EDGE = 4096;

/**
 * The two models offered, smaller first.
 *
 * Same architecture and the same input, so only the file differs. `bytes` is
 * what the visitor downloads once, and is shown before they commit to it.
 */
export const MODELS = [
    {
        id: 'fast',
        file: '/models/u2netp.onnx',
        label: 'Fast',
        bytes: 4574861,
        note: 'Small download, good on clear subjects',
    },
    {
        id: 'accurate',
        file: '/models/silueta.onnx',
        label: 'Accurate',
        bytes: 44173029,
        note: 'Larger download, better on hair and thin edges',
    },
];

/** The picture as the tensor the network expects: 1x3x320x320, normalised. */
function toTensor(ort, image) {
    const canvas = document.createElement('canvas');
    canvas.width = INPUT_SIZE;
    canvas.height = INPUT_SIZE;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // Squashed to a square rather than letterboxed. That is how the model was
    // trained and how every reference implementation feeds it, and the mask is
    // stretched back the same way afterwards.
    ctx.drawImage(image, 0, 0, INPUT_SIZE, INPUT_SIZE);

    const { data } = ctx.getImageData(0, 0, INPUT_SIZE, INPUT_SIZE);
    const pixels = INPUT_SIZE * INPUT_SIZE;

    // Scaled by the brightest channel present rather than by 255, which is what
    // the reference implementations do. On an evenly exposed picture the two
    // are the same; on a dark one this lifts it into the range the network saw
    // during training.
    let peak = 0;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i] > peak) peak = data[i];
        if (data[i + 1] > peak) peak = data[i + 1];
        if (data[i + 2] > peak) peak = data[i + 2];
    }
    if (peak === 0) peak = 1;

    // Channel first: all the reds, then all the greens, then all the blues.
    const input = new Float32Array(3 * pixels);
    for (let p = 0; p < pixels; p += 1) {
        const source = p * 4;
        for (let c = 0; c < 3; c += 1) {
            input[c * pixels + p] = (data[source + c] / peak - MEAN[c]) / STD[c];
        }
    }

    return new ort.Tensor('float32', input, [1, 3, INPUT_SIZE, INPUT_SIZE]);
}

/**
 * The network's first output as a 320x320 grayscale mask.
 *
 * The raw numbers are not bounded to a usable range, so they are stretched
 * between their own smallest and largest value. Without that the mask on a
 * low-contrast picture comes out uniformly grey and everything turns
 * half-transparent.
 */
function toMask(output) {
    const values = output.data;
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < values.length; i += 1) {
        if (values[i] < min) min = values[i];
        if (values[i] > max) max = values[i];
    }

    const span = max - min || 1;
    const mask = new Uint8ClampedArray(INPUT_SIZE * INPUT_SIZE * 4);
    for (let p = 0; p < INPUT_SIZE * INPUT_SIZE; p += 1) {
        const value = ((values[p] - min) / span) * 255;
        // Written into all three colour channels, opaque. The mask is scaled up
        // by drawing it, and a browser interpolates colour smoothly while it
        // would not blend into a transparent neighbour the same way.
        const target = p * 4;
        mask[target] = value;
        mask[target + 1] = value;
        mask[target + 2] = value;
        mask[target + 3] = 255;
    }

    return new ImageData(mask, INPUT_SIZE, INPUT_SIZE);
}

/** The mask, scaled to the output size, as one byte of coverage per pixel. */
function scaleMask(maskData, width, height) {
    const small = document.createElement('canvas');
    small.width = INPUT_SIZE;
    small.height = INPUT_SIZE;
    small.getContext('2d').putImageData(maskData, 0, 0);

    const large = document.createElement('canvas');
    large.width = width;
    large.height = height;

    const ctx = large.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(small, 0, 0, width, height);

    return ctx.getImageData(0, 0, width, height).data;
}

/**
 * Cut the background out of a decoded image.
 *
 * @param {ImageBitmap|HTMLImageElement} image
 * @param {object}   options
 * @param {object}   options.model       One entry of MODELS.
 * @param {Function} options.onDownload  Model download progress, 0 to 1.
 * @param {Function} options.onStage     Called with a short status string.
 * @returns {Promise<HTMLCanvasElement>} The subject on a transparent canvas.
 */
export async function removeBackground(image, { model, onDownload, onStage } = {}) {
    const chosen = model || MODELS[0];

    if (onStage) onStage('Preparing the model');
    const session = await sessionFor(chosen, onDownload);
    const ort = await loadRuntime();

    if (onStage) onStage('Looking at the image');
    const feeds = { [session.inputNames[0]]: toTensor(ort, image) };
    const results = await session.run(feeds);
    const maskData = toMask(results[session.outputNames[0]]);

    if (onStage) onStage('Cutting out the subject');
    const sourceWidth = image.width || image.naturalWidth;
    const sourceHeight = image.height || image.naturalHeight;
    const scale = Math.min(1, MAX_OUTPUT_EDGE / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, width, height);

    const picture = ctx.getImageData(0, 0, width, height);
    const coverage = scaleMask(maskData, width, height);
    for (let i = 0; i < picture.data.length; i += 4) {
        // Multiplied into whatever alpha the picture already had, so a PNG that
        // arrived with transparent corners keeps them.
        picture.data[i + 3] = (picture.data[i + 3] * coverage[i]) / 255;
    }
    ctx.putImageData(picture, 0, 0);

    return canvas;
}
